import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LOCALE, LOCALES } from './constants';
import { detectLocale } from './detectLocale';
import { setStoredLocale } from './localeStorage';
import { setActiveLocale } from '../content/runtime';
import '../content';
import { syncContentfulLocale } from '../content/contentful/sync';
import {
  getLocaleFromPathname,
  isPublicLocaleRoute,
  publicAuthFallbackPath,
  stripLocale,
  withLocale,
} from './paths';

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isPublicLocaleRoute: false,
  withLocale: (path) => path,
  contentRevision: 0,
});

function applyDocumentLang(locale) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export function LocaleProvider({ children }) {
  const location = useLocation();
  const [locale, setLocaleState] = useState(() => detectLocale());
  const [contentRevision, setContentRevision] = useState(0);
  setActiveLocale(locale);

  const urlLocale = getLocaleFromPathname(location.pathname);
  const onPublicLocaleRoute = Boolean(urlLocale) && isPublicLocaleRoute(location.pathname);

  // URL wins on public locale routes
  useEffect(() => {
    if (urlLocale && LOCALES.includes(urlLocale) && urlLocale !== locale) {
      setActiveLocale(urlLocale);
      setLocaleState(urlLocale);
      setStoredLocale(urlLocale);
      applyDocumentLang(urlLocale);
    }
  }, [urlLocale, locale]);

  // Keep document lang in sync on app routes / initial load
  useEffect(() => {
    applyDocumentLang(locale);
  }, [locale]);

  useEffect(() => {
    const controller = new AbortController();
    syncContentfulLocale(locale, controller.signal).finally(() => {
      if (!controller.signal.aborted) {
        setContentRevision((n) => n + 1);
      }
    });
    return () => controller.abort();
  }, [locale]);

  const setLocale = useCallback(
    (next) => {
      if (!LOCALES.includes(next) || next === locale) {
        return;
      }

      setActiveLocale(next);
      setStoredLocale(next);

      if (onPublicLocaleRoute) {
        const rest = stripLocale(location.pathname);
        const targetPath =
          rest === '/' ? publicAuthFallbackPath(location.search) : rest;
        window.location.assign(
          `${withLocale(next, targetPath)}${location.search}${location.hash}`
        );
        return;
      }

      window.location.reload();
    },
    [locale, location.hash, location.pathname, location.search, onPublicLocaleRoute]
  );

  const withLocalePath = useCallback(
    (path) => withLocale(locale, path),
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      isPublicLocaleRoute: onPublicLocaleRoute,
      withLocale: withLocalePath,
      contentRevision,
    }),
    [locale, setLocale, onPublicLocaleRoute, withLocalePath, contentRevision]
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

export default LocaleContext;
